import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Layout,
  Drawer,
  Button,
  Input,
  Tooltip,
  Modal,
} from "antd";

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  CalendarOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  ApartmentOutlined,
  BookOutlined,
  IdcardOutlined,
  CheckSquareOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import {
  FaGraduationCap,
  FaUserGraduate,
  FaClipboardCheck,
  FaUserShield,
} from "react-icons/fa";

import { motion } from "framer-motion";

import OverViewDean from "./pages/OverViewDean";

import UserSearchBox from "../SchoolDashApp/UserDetails/UserSearchBox";
import UserDetailsPage from "../SchoolDashApp/UserDetails/UserDetailsPage";
import ProfileImage from "./ProfileImage";

import AdmissionForm from "./NewStudents/AdmissionForm/AdmissionForm";
import StudentAdmission from "./NewStudents/AdmissionForm/StudentAdmission";

import StudentStatusSummary from "../SchoolDashApp/StudentStatusSummary";
import ComplaintUnit from "../Dashboard/ComplaintUnit";

import GradeDistribution from "../pages/Reportcard/GradeDistribution";
import HighAveragesPage from "../SchoolDashApp/ReportCard/HighAveragesPage";
import TopStudentPerPeriod from "../SchoolDashApp/ReportCard/TopStudentPerPeriod";
import PeriodReport from "../pages/Reportcard/PeriodReport";
import ReportCard from "./ReportCard";

import ClassViewList from "../SchoolDashApp/Class/ClassViewList";

import AttendanceRecord from "../pages/Attendance/AttendanceRecord";
import AttendanceData from "../pages/Attendance/AttendanceData";

import CertificateDashboard from "./certificate/CertificateDashboard";
import StudentsIDCard from "../pages/Students/IdCards/StudentsIDCard";

import AssignmentView from "../pages/Teachers/AssignmentView";
import GetStudentNotes from "../pages/Teachers/GetStudentNotes";
import TestAttachmentView from "../SchoolDashApp/TestAttachmentView";

import PostTodoList from "./CalenderAndTodo/PostTodoList";

import Darkmode from "./Darkmode/Darkmode";
import NotificationIcon from "../components/ChatApp/NotificationIcon";
import Notice from "../components/ChatApp/Notice";

import logo from "./exellentkidslogo.png";

const { Header, Sider, Content } = Layout;

/* ============================================================
   Zion Praise Academy - CRM School System
   DEAN OF STUDENTS COLOR SYSTEM
============================================================ */

const COLORS = {
  navy: "#061A33",
  navyDeep: "#031226",
  navyDark: "#04162B",
  navyMid: "#0A2A4F",
  navyLight: "#123E68",

  cache: "#B8A878",
  cacheDark: "#8F8058",
  cacheLight: "#D4C79F",

  gold: "#D4A017",
  goldBright: "#F2C94C",
  yellow: "#FACC15",
  yellowSoft: "#FFF4B8",

  white: "#FFFFFF",
  ivory: "#FCFBF7",

  page: "#F5F7FA",
  soft: "#F8F9FB",

  text: "#102033",
  muted: "#66758A",
  border: "#DCE2E9",

  success: "#B18A19",
  warning: "#D4A017",
  error: "#B64040",
  info: "#1D5D8F",

  sidebar: "#031226",
  sidebarAlt: "#061A33",
  sidebarBorder: "#183451",

  sidebarText: "#F7F8FA",
  sidebarMuted: "#AAB7C7",
  sidebarIcon: "#B8C5D3",
  sidebarBright: "#F2C94C",

  black: "#020A14",
  gray: "#AAB4C0",

  shadow:
    "0 12px 35px rgba(3,18,38,.10)",
};

/*
|--------------------------------------------------------------------------
| DEAN OF STUDENTS MENU
|--------------------------------------------------------------------------
*/

const menuItems = [
  {
    key: "overview",
    icon: <DashboardOutlined />,
    label: "Student Dashboard",
  },

  {
    key: "admissionlist",
    icon: <TeamOutlined />,
    label: "Students List",
  },

  {
    key: "studentsadmission",
    icon: <UsergroupAddOutlined />,
    label: "Student Admission",
  },

  {
    key: "statusrecord",
    icon: <SolutionOutlined />,
    label: "Student Status Record",
  },

  {
    key: "complaintunit",
    icon: <FaUserShield />,
    label: "Student Complaints & Welfare",
  },

  {
    key: "attendancerecord",
    icon: <CalendarOutlined />,
    label: "Student Attendance",
  },

  {
    key: "attendancedata",
    icon: <ClipboardIcon />,
    label: "Attendance Data",
  },

  {
    key: "classviewlist",
    icon: <ApartmentOutlined />,
    label: "Grade Classes",
  },

  {
    key: "gradedistribution",
    icon: <FaGraduationCap />,
    label: "Grade Distribution",
  },

  {
    key: "periodicreport",
    icon: <FaClipboardCheck />,
    label: "Period Reports",
  },

  {
    key: "reportcard",
    icon: <FaUserGraduate />,
    label: "Student Report Cards",
  },

  {
    key: "topstudents",
    icon: <FaGraduationCap />,
    label: "Top Students",
  },

  {
    key: "highaverages",
    icon: <FaGraduationCap />,
    label: "High Averages",
  },

  {
    key: "subjectviewlist",
    icon: <BookOutlined />,
    label: "Courses Listing",
  },

  {
    key: "approvedlearningcourse",
    icon: <BookOutlined />,
    label: "Approved Learning Courses",
  },

  {
    key: "studentsassignments",
    icon: <FaClipboardCheck />,
    label: "Student Assignments",
  },

  {
    key: "studentsnotes",
    icon: <BookOutlined />,
    label: "Student Notes",
  },

  {
    key: "testattachmentview",
    icon: <FaClipboardCheck />,
    label: "Student Test Attachments",
  },

  {
    key: "studentscertificate",
    icon: <SafetyCertificateOutlined />,
    label: "Student Certificates",
  },

  {
    key: "studentsidcards",
    icon: <IdcardOutlined />,
    label: "Student ID Cards",
  },
];

function ClipboardIcon() {
  return <FaClipboardCheck />;
}

const navigationSections = [
  {
    key: "command",
    title: "STUDENT COMMAND CENTER",
    items: ["overview"],
  },

  {
    key: "students",
    title: "STUDENT MANAGEMENT",
    items: [
      "admissionlist",
      "studentsadmission",
      "statusrecord",
      "complaintunit",
    ],
  },

  {
    key: "attendance",
    title: "ATTENDANCE & WELLBEING",
    items: [
      "attendancerecord",
      "attendancedata",
    ],
  },

  {
    key: "academic",
    title: "ACADEMIC PROGRESS",
    items: [
      "classviewlist",
      "gradedistribution",
      "periodicreport",
      "reportcard",
      "topstudents",
      "highaverages",
    ],
  },

  {
    key: "learning",
    title: "STUDENT LEARNING",
    items: [
      "subjectviewlist",
      "approvedlearningcourse",
      "studentsassignments",
      "studentsnotes",
      "testattachmentview",
    ],
  },

  {
    key: "student-services",
    title: "STUDENT SERVICES",
    items: [
      "studentscertificate",
      "studentsidcards",
    ],
  },
];

const DeanDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [darkMode, setDarkMode] = useState(false);

  const [activePage, setActivePage] =
    useState("overview");

  const [selectedUser, setSelectedUser] =
    useState(null);

  const [menuSearch, setMenuSearch] =
    useState("");

  const [todoOpen, setTodoOpen] =
    useState(false);

  const [
    passwordSettingOpen,
    setPasswordSettingOpen,
  ] = useState(false);

  const [isMobile, setIsMobile] =
    useState(
      typeof window !== "undefined"
        ? window.innerWidth < 900
        : false
    );

  const [isNavigating, setIsNavigating] =
    useState(false);

  const [pendingPage, setPendingPage] =
    useState(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile =
        window.innerWidth < 900;

      setIsMobile(mobile);

      if (mobile) {
        setCollapsed(false);
      }
    };

    handleResize();

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, []);

  useEffect(() => {
    const style =
      document.createElement("style");

    style.id =
      "dean-student-dashboard-styles";

    style.innerHTML = `
      * {
        box-sizing: border-box;
      }

      html,
      body,
      #root {
        margin: 0;
        min-height: 100%;
      }

      body {
        overflow: hidden;
        background:
          linear-gradient(
            135deg,
            #F5F7FA 0%,
            #FCFBF7 48%,
            #F3F5F8 100%
          );
        color: #102033;
      }

      ::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      ::-webkit-scrollbar-track {
        background: transparent;
      }

      ::-webkit-scrollbar-thumb {
        background: #B8A878;
        border-radius: 20px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: #D4A017;
      }

      * {
        scrollbar-width: thin;
        scrollbar-color: #B8A878 transparent;
      }

      /* ======================================================
         MENU BUTTON
      ====================================================== */

      .dean-menu-button {
        width: 39px !important;
        height: 39px !important;
        min-width: 39px !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        flex-shrink: 0 !important;
        border-radius: 9px !important;
        border: 1px solid #DCE2E9 !important;
        background: #FFFFFF !important;
        color: #061A33 !important;
        box-shadow: none !important;
        transition: all 180ms ease !important;
      }

      .dean-menu-button:hover {
        color: #D4A017 !important;
        border-color: #D4C79F !important;
        background: #FCFBF7 !important;
      }

      .dean-dark .dean-menu-button {
        background: #0A2A4F !important;
        border-color: #244767 !important;
        color: #FFFFFF !important;
      }

      /* ======================================================
         HEADER SEARCH
      ====================================================== */

      .dean-search-box {
        min-width: 0;
      }

      .dean-search-box .ant-input-affix-wrapper {
        min-height: 39px !important;
        border-radius: 9px !important;
        border: 1px solid #DCE2E9 !important;
        background: #FFFFFF !important;
        box-shadow: none !important;
      }

      .dean-search-box .ant-input-affix-wrapper:hover {
        border-color: #D4C79F !important;
      }

      .dean-search-box
      .ant-input-affix-wrapper-focused {
        border-color: #D4A017 !important;
        box-shadow:
          0 0 0 2px rgba(212,160,23,.12) !important;
      }

      .dean-search-box .ant-input {
        color: #102033 !important;
      }

      .dean-search-box
      .ant-input::placeholder {
        color: #8793A3 !important;
      }

      .dean-dark
      .dean-search-box
      .ant-input-affix-wrapper {
        background: #0A2A4F !important;
        border-color: #244767 !important;
      }

      .dean-dark
      .dean-search-box
      .ant-input {
        color: #FFFFFF !important;
      }

      /* ======================================================
         SIDEBAR SEARCH
      ====================================================== */

      .dean-sidebar-search
      .ant-input-affix-wrapper {
        min-height: 39px !important;
        border-radius: 8px !important;
        background: #061A33 !important;
        border: 1px solid #183451 !important;
        box-shadow: none !important;
      }

      .dean-sidebar-search
      .ant-input-affix-wrapper:hover {
        border-color: #B8A878 !important;
      }

      .dean-sidebar-search .ant-input {
        background: transparent !important;
        color: #FFFFFF !important;
      }

      .dean-sidebar-search
      .ant-input::placeholder {
        color: #8D9CAF !important;
      }

      .dean-sidebar-search
      .ant-input-affix-wrapper-focused {
        border-color: #D4A017 !important;
        box-shadow:
          0 0 0 2px rgba(212,160,23,.15) !important;
      }

      /* ======================================================
         SIDEBAR SCROLL
      ====================================================== */

      .dean-sidebar-scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 28px;
      }

      .dean-sidebar-scroll::-webkit-scrollbar {
        width: 6px;
      }

      .dean-sidebar-scroll::-webkit-scrollbar-thumb {
        background:
          linear-gradient(
            180deg,
            rgba(212,160,23,.90),
            rgba(184,168,120,.70)
          );
        border-radius: 20px;
      }

      /* ======================================================
         SECTION TITLES
      ====================================================== */

      .dean-section-title {
        padding: 16px 18px 7px;
        color: #B8A878;
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 1.25px;
        user-select: none;
      }

      /* ======================================================
         SIDEBAR NAVIGATION
      ====================================================== */

      .dean-nav-item {
        position: relative;
        width: calc(100% - 24px);
        min-height: 43px;
        margin: 2px 12px;
        padding: 0 12px;
        border: 0;
        border-radius: 8px;
        display: flex;
        align-items: center;
        gap: 11px;
        color: #C6D0DC;
        background: transparent;
        cursor: pointer;
        text-align: left;
        font-family: inherit;
        font-size: 13px;
        font-weight: 500;
        transition:
          background 150ms ease,
          color 150ms ease;
      }

      .dean-nav-item:hover {
        background:
          linear-gradient(
            90deg,
            rgba(212,160,23,.20),
            rgba(184,168,120,.07)
          );
        color: #FFFFFF;
      }

      .dean-nav-item.active {
        background:
          linear-gradient(
            90deg,
            rgba(6,42,79,.98),
            rgba(18,62,104,.82),
            rgba(212,160,23,.10)
          );
        color: #FFFFFF;
        font-weight: 700;
        box-shadow:
          0 6px 18px rgba(0,0,0,.24);
      }

      .dean-nav-item.active::before {
        content: "";
        position: absolute;
        left: 0;
        top: 7px;
        bottom: 7px;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background:
          linear-gradient(
            180deg,
            #FACC15,
            #D4A017
          );
        box-shadow:
          0 0 11px rgba(242,201,76,.55);
      }

      .dean-nav-icon {
        width: 28px;
        height: 28px;
        min-width: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 7px;
        color: #B8C5D3;
        font-size: 15px;
        transition: all 150ms ease;
      }

      .dean-nav-item:hover
      .dean-nav-icon,
      .dean-nav-item.active
      .dean-nav-icon {
        color: #F2C94C;
        background:
          rgba(242,201,76,.10);
      }

      .dean-nav-label {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        line-height: 1.2;
      }

      .dean-nav-chevron {
        margin-left: auto;
        opacity: 0;
        color: #F2C94C;
        transition: all 150ms ease;
      }

      .dean-nav-item:hover
      .dean-nav-chevron,
      .dean-nav-item.active
      .dean-nav-chevron {
        opacity: 1;
      }

      .dean-collapsed-item {
        width: calc(100% - 18px);
        justify-content: center;
        margin-left: 9px;
        margin-right: 9px;
        padding: 0;
      }

      .dean-collapsed-item
      .dean-nav-chevron,
      .dean-collapsed-item
      .dean-nav-label {
        display: none;
      }

      .dean-collapsed-item
      .dean-nav-icon {
        width: 34px;
        height: 34px;
        min-width: 34px;
      }

      /* ======================================================
         DEEP NAVY SIDEBAR
      ====================================================== */

      .dean-sidebar-shell {
        position: relative;
        height: 100vh;
        min-height: 100vh;
        overflow: hidden;
        background:
          radial-gradient(
            circle at 15% 8%,
            rgba(212,160,23,.10),
            transparent 24%
          ),
          radial-gradient(
            circle at 90% 85%,
            rgba(184,168,120,.08),
            transparent 25%
          ),
          linear-gradient(
            145deg,
            #020D1C 0%,
            #031226 44%,
            #061A33 75%,
            #031226 100%
          );
      }

      .dean-sidebar-content {
        position: relative;
        z-index: 2;
        height: 100%;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      /* ======================================================
         HEADER ACTIONS
      ====================================================== */

      .dean-header-action {
        width: 39px;
        height: 39px;
        min-width: 39px;
        padding: 0;
        border-radius: 9px;
        border: 1px solid #DCE2E9;
        background: #FFFFFF;
        color: #061A33;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 160ms ease;
      }

      .dean-header-action:hover {
        color: #D4A017;
        border-color: #D4C79F;
        background: #FCFBF7;
      }

      .dean-dark .dean-header-action {
        background: #0A2A4F;
        border-color: #244767;
        color: #FFFFFF;
      }

      /* ======================================================
         PROFILE
      ====================================================== */

      .dean-profile-container {
        width: 40px;
        height: 40px;
        min-width: 40px;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #D4A017;
        background: #FFFFFF;
        box-shadow:
          0 3px 12px rgba(3,18,38,.18);
        flex-shrink: 0;
      }

      .dean-profile-avatar,
      .dean-profile-avatar > * {
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }

      .dean-profile-container img {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        display: block !important;
      }

      /* ======================================================
         WORKSPACE
      ====================================================== */

      .dean-workspace {
        border: 1px solid #DCE2E9;
        background: #FFFFFF;
        border-radius: 11px;
        min-height: calc(100vh - 111px);
        box-shadow:
          0 4px 18px rgba(3,18,38,.035);
        position: relative;
        overflow: hidden;
      }

      .dean-workspace::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background:
          linear-gradient(
            90deg,
            #061A33,
            #D4A017,
            #FACC15,
            #B8A878
          );
        opacity: .85;
        pointer-events: none;
      }

      .dean-workspace-dark {
        background: #061A33;
        border-color: #244767;
      }

      /* ======================================================
         PAGE HEADING
      ====================================================== */

      .dean-page-heading {
        min-height: 34px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 8px;
        padding: 0 2px;
      }

      .dean-page-heading-left {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .dean-page-heading-line {
        width: 4px;
        height: 18px;
        min-width: 4px;
        border-radius: 3px;
        background:
          linear-gradient(
            180deg,
            #061A33,
            #D4A017
          );
      }

      .dean-page-heading-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #102033;
        font-size: 14px;
        font-weight: 750;
      }

      .dean-dark .dean-page-heading-title {
        color: #FFFFFF;
      }

      .dean-page-heading-meta {
        color: #8F8058;
        font-size: 10px;
        font-weight: 700;
        white-space: nowrap;
      }

      /* ======================================================
         NAVIGATION PROGRESS
      ====================================================== */

      .dean-navigation-progress {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        z-index: 1000;
        overflow: hidden;
        background:
          rgba(212,160,23,.12);
      }

      .dean-navigation-progress::after {
        content: "";
        position: absolute;
        inset: 0;
        width: 100%;
        background:
          linear-gradient(
            90deg,
            #061A33,
            #D4A017,
            #FACC15
          );
        transform-origin: left;
        animation:
          deanNavigationProgress
          2s ease-out forwards;
      }

      @keyframes deanNavigationProgress {
        0% {
          transform: scaleX(0);
        }

        45% {
          transform: scaleX(.52);
        }

        75% {
          transform: scaleX(.78);
        }

        100% {
          transform: scaleX(1);
        }
      }

      .dean-loading-label {
        position: absolute;
        top: 11px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1001;
        padding: 5px 10px;
        border-radius: 6px;
        background:
          linear-gradient(
            135deg,
            #020D1C,
            #061A33
          );
        color: #FFFFFF;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: .75px;
        text-transform: uppercase;
        white-space: nowrap;
        box-shadow:
          0 5px 15px rgba(0,0,0,.25);
        border:
          1px solid rgba(212,160,23,.25);
      }

      /* ======================================================
         PASSWORD MODAL
      ====================================================== */

      .dean-password-modal .ant-modal-content {
        border-radius: 15px !important;
        overflow: hidden !important;
        padding: 0 !important;
      }

      .dean-password-modal .ant-modal-body {
        padding: 0 !important;
      }

      /* ======================================================
         MOBILE
      ====================================================== */

      @media (max-width: 899px) {
        .dean-header-action {
          width: 36px;
          height: 36px;
          min-width: 36px;
        }

        .dean-profile-container {
          width: 36px;
          height: 36px;
          min-width: 36px;
        }

        .dean-workspace {
          min-height: calc(100vh - 96px);
          border-radius: 8px;
        }

        .dean-page-heading-title {
          font-size: 13px;
        }

        .dean-page-heading-meta {
          display: none;
        }
      }

      @media (max-width: 650px) {
        .dean-header-action {
          width: 34px;
          height: 34px;
          min-width: 34px;
        }

        .dean-profile-container {
          width: 34px;
          height: 34px;
          min-width: 34px;
        }

        .dean-menu-button {
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
        }
      }
    `;

    document.head.appendChild(style);

    return () => {
      const existing =
        document.getElementById(
          "dean-student-dashboard-styles"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, []);

  const theme = useMemo(
    () => ({
      darkMode,

      background: darkMode
        ? "#020D1C"
        : COLORS.page,

      sidebar: COLORS.sidebar,

      header: darkMode
        ? "#061A33"
        : COLORS.white,

      card: darkMode
        ? "#061A33"
        : COLORS.white,

      inputBackground: darkMode
        ? "#0A2A4F"
        : COLORS.white,

      text: darkMode
        ? "#FFFFFF"
        : COLORS.text,

      secondary: darkMode
        ? "#B8C5D3"
        : COLORS.muted,

      border: darkMode
        ? "#244767"
        : COLORS.border,

      primary: COLORS.navy,
      navy: COLORS.navy,
      blue: COLORS.navyLight,
      gold: COLORS.gold,
      yellow: COLORS.yellow,
      orange: COLORS.gold,
      green: COLORS.cache,
      sky: COLORS.info,
      antique: COLORS.cache,
    }),
    [darkMode]
  );

  const menuLookup = useMemo(
    () =>
      Object.fromEntries(
        menuItems.map((item) => [
          item.key,
          item,
        ])
      ),
    []
  );

  const filteredSections = useMemo(() => {
    const query = menuSearch
      .trim()
      .toLowerCase();

    if (!query) {
      return navigationSections;
    }

    return navigationSections
      .map((section) => ({
        ...section,

        items: section.items.filter(
          (key) =>
            menuLookup[key]?.label
              ?.toLowerCase()
              .includes(query)
        ),
      }))
      .filter(
        (section) =>
          section.items.length > 0
      );
  }, [menuSearch, menuLookup]);

  const activeTitle =
    selectedUser
      ? "Student / User Details"
      : menuLookup[activePage]?.label ||
        "Student Dashboard";

  const renderPage = () => {
    if (selectedUser) {
      return (
        <UserDetailsPage
          theme={theme}
          selectedUser={selectedUser}
        />
      );
    }

    switch (activePage) {
      case "overview":
        return (
          <OverViewDean
            theme={theme}
          />
        );

      case "studentsadmission":
        return (
          <AdmissionForm
            theme={theme}
          />
        );

      case "admissionlist":
        return (
          <StudentAdmission
            theme={theme}
          />
        );

      case "statusrecord":
        return (
          <StudentStatusSummary
            theme={theme}
          />
        );

      case "complaintunit":
        return (
          <ComplaintUnit
            theme={theme}
          />
        );

      case "attendancerecord":
        return (
          <AttendanceRecord
            theme={theme}
          />
        );

      case "attendancedata":
        return (
          <AttendanceData
            theme={theme}
          />
        );

      case "classviewlist":
        return (
          <ClassViewList
            theme={theme}
          />
        );

      case "gradedistribution":
        return (
          <GradeDistribution
            theme={theme}
          />
        );

      case "periodicreport":
        return (
          <PeriodReport
            theme={theme}
          />
        );

      case "reportcard":
        return (
          <ReportCard
            theme={theme}
          />
        );

      case "topstudents":
        return (
          <TopStudentPerPeriod
            theme={theme}
          />
        );

      case "highaverages":
        return (
          <HighAveragesPage
            theme={theme}
          />
        );

      case "studentsassignments":
        return (
          <AssignmentView
            theme={theme}
          />
        );

      case "studentsnotes":
        return (
          <GetStudentNotes
            theme={theme}
          />
        );

      case "testattachmentview":
        return (
          <TestAttachmentView
            theme={theme}
          />
        );

      case "studentscertificate":
        return (
          <CertificateDashboard
            theme={theme}
          />
        );

      case "studentsidcards":
        return (
          <StudentsIDCard
            theme={theme}
          />
        );

      default:
        return (
          <OverViewDean
            theme={theme}
          />
        );
    }
  };

  const handleNavigation = (key) => {
    if (isNavigating) {
      return;
    }

    if (
      key === activePage &&
      !selectedUser
    ) {
      if (isMobile) {
        setDrawerOpen(false);
      }

      return;
    }

    setPendingPage(key);
    setIsNavigating(true);
    setSelectedUser(null);
    setMenuSearch("");

    if (isMobile) {
      setDrawerOpen(false);
    }

    window.setTimeout(() => {
      setActivePage(key);
      setPendingPage(null);
      setIsNavigating(false);
    }, 500);
  };

  const renderNavigationItem = (item) => {
    const active =
      activePage === item.key &&
      !selectedUser;

    return (
      <Tooltip
        key={item.key}
        title={
          collapsed
            ? item.label
            : ""
        }
        placement="right"
      >
        <button
          type="button"
          className={[
            "dean-nav-item",
            active ? "active" : "",
            collapsed
              ? "dean-collapsed-item"
              : "",
          ].join(" ")}
          onClick={() =>
            handleNavigation(
              item.key
            )
          }
          disabled={isNavigating}
          aria-current={
            active
              ? "page"
              : undefined
          }
        >
          <span className="dean-nav-icon">
            {item.icon}
          </span>

          <span className="dean-nav-label">
            {item.label}
          </span>

          <span className="dean-nav-chevron">
            ›
          </span>
        </button>
      </Tooltip>
    );
  };

  const sidebar = (
    <div className="dean-sidebar-shell">
      <div className="dean-sidebar-content">
        <div
          style={{
            height: "100%",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* ==================================================
              SIDEBAR BRAND
          ================================================== */}

          <div
            style={{
              height: 72,
              minHeight: 72,
              display: "flex",
              alignItems: "center",
              justifyContent:
                collapsed
                  ? "center"
                  : "flex-start",
              gap: 11,
              padding:
                collapsed
                  ? "0 10px"
                  : "0 18px",
              borderBottom:
                "1px solid rgba(212,160,23,.16)",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                minWidth: 38,
                borderRadius: 8,
                padding: 2,
                background: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                border:
                  "1px solid rgba(212,160,23,.55)",
                boxShadow:
                  "0 4px 14px rgba(0,0,0,.30)",
              }}
            >
              <img
                src={logo}
                alt="Zion Praise Academy - CRM School System logo"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 6,
                  display: "block",
                }}
              />
            </div>

            {!collapsed && (
              <div
                style={{
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    color: "#FFFFFF",
                    fontSize: 13,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Zion Praise Academy - CRM School System
                </div>

                <div
                  style={{
                    marginTop: 3,
                    color: "#F2C94C",
                    fontSize: 8,
                    fontWeight: 800,
                    letterSpacing: 1.05,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  DEAN OF STUDENTS
                </div>
              </div>
            )}
          </div>

          {/* ==================================================
              SIDEBAR SEARCH
          ================================================== */}

          {!collapsed && (
            <div
              className="dean-sidebar-search"
              style={{
                padding:
                  "13px 12px 6px",
                flexShrink: 0,
              }}
            >
              <Input
                allowClear
                value={menuSearch}
                onChange={(event) =>
                  setMenuSearch(
                    event.target.value
                  )
                }
                placeholder="Find student module..."
                prefix={
                  <SearchOutlined
                    style={{
                      color:
                        COLORS.goldBright,
                    }}
                  />
                }
              />
            </div>
          )}

          {/* ==================================================
              NAVIGATION
          ================================================== */}

          <div className="dean-sidebar-scroll">
            {filteredSections.length ===
              0 && (
              <div
                style={{
                  padding:
                    "35px 20px",
                  textAlign: "center",
                  color: "#8D9CAF",
                  fontSize: 11,
                }}
              >
                No student module found
              </div>
            )}

            {filteredSections.map(
              (section) => {
                const items =
                  section.items
                    .map(
                      (key) =>
                        menuLookup[key]
                    )
                    .filter(Boolean);

                if (!items.length) {
                  return null;
                }

                return (
                  <div
                    key={section.key}
                  >
                    {!collapsed ? (
                      <div className="dean-section-title">
                        {section.title}
                      </div>
                    ) : (
                      <div
                        style={{
                          height: 12,
                        }}
                      />
                    )}

                    {items.map(
                      renderNavigationItem
                    )}
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={
        darkMode
          ? "dean-dark"
          : ""
      }
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        overflow: "hidden",
        background:
          theme.background,
      }}
    >
      <Layout
        style={{
          height: "100vh",
          minHeight: "100vh",
          background:
            theme.background,
        }}
      >
        {!isMobile && (
          <Sider
            width={292}
            collapsedWidth={78}
            collapsed={collapsed}
            trigger={null}
            style={{
              position: "fixed",
              top: 0,
              bottom: 0,
              left: 0,
              height: "100vh",
              zIndex: 100,
              overflow: "hidden",
              background:
                COLORS.sidebar,
            }}
          >
            {sidebar}
          </Sider>
        )}

        {isMobile && (
          <Drawer
            open={drawerOpen}
            placement="left"
            width={310}
            closable={false}
            onClose={() =>
              setDrawerOpen(false)
            }
            styles={{
              body: {
                padding: 0,
                background:
                  COLORS.sidebar,
                overflow: "hidden",
              },
            }}
          >
            <div
              style={{
                position:
                  "absolute",
                top: 13,
                right: 13,
                zIndex: 20,
              }}
            >
              <Button
                type="text"
                icon={
                  <CloseOutlined />
                }
                onClick={() =>
                  setDrawerOpen(false)
                }
                style={{
                  width: 34,
                  height: 34,
                  color: "#FFFFFF",
                  background:
                    "rgba(212,160,23,.12)",
                  border:
                    "1px solid rgba(212,160,23,.25)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                }}
              />
            </div>

            {sidebar}
          </Drawer>
        )}

        <Layout
          style={{
            marginLeft: isMobile
              ? 0
              : collapsed
                ? 78
                : 292,
            height: "100vh",
            minHeight: "100vh",
            background:
              theme.background,
            transition:
              "margin-left 180ms ease",
          }}
        >
          <Header
            style={{
              height: 68,
              minHeight: 68,
              flexShrink: 0,
              padding: isMobile
                ? "0 9px"
                : "0 16px",
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 6 : 10,
              background:
                theme.header,
              borderBottom:
                `1px solid ${theme.border}`,
              position: "relative",
              zIndex: 30,
            }}
          >
            {isNavigating && (
              <>
                <div className="dean-navigation-progress" />

                <div className="dean-loading-label">
                  Opening{" "}
                  {menuLookup[
                    pendingPage
                  ]?.label ||
                    "student module"}
                </div>
              </>
            )}

            <Button
              type="text"
              className="dean-menu-button"
              icon={
                isMobile ? (
                  <MenuUnfoldOutlined />
                ) : collapsed ? (
                  <MenuUnfoldOutlined />
                ) : (
                  <MenuFoldOutlined />
                )
              }
              onClick={() => {
                if (isMobile) {
                  setDrawerOpen(true);
                } else {
                  setCollapsed(
                    (value) =>
                      !value
                  );
                }
              }}
            />

            <div
              className="dean-search-box"
              style={{
                flex: 1,
                minWidth: 0,
                maxWidth: isMobile
                  ? 270
                  : 450,
              }}
            >
              <UserSearchBox
                theme={theme}
                setSelectedUser={
                  setSelectedUser
                }
                setActiveComponent={
                  setActivePage
                }
              />
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 0,
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <Tooltip title="Tasks">
                <button
                  type="button"
                  className="dean-header-action"
                  onClick={() =>
                    setTodoOpen(true)
                  }
                  aria-label="Tasks"
                >
                  <CheckSquareOutlined />
                </button>
              </Tooltip>

              <PostTodoList
                open={todoOpen}
                setOpen={setTodoOpen}
              />

              <Darkmode
                darkMode={darkMode}
                setDarkMode={
                  setDarkMode
                }
              />

              {!isMobile && (
                <>
                  <NotificationIcon
                    theme={theme}
                  />

                  <Notice />
                </>
              )}

              <Tooltip title="Profile">
                <div className="dean-profile-container">
                  <div className="dean-profile-avatar">
                    <ProfileImage
                      theme={theme}
                      header
                    />
                  </div>
                </div>
              </Tooltip>
            </div>
          </Header>

          <Content
            style={{
              background:
                theme.background,
              padding: isMobile
                ? "9px 8px 12px"
                : "14px 16px 18px",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <div className="dean-page-heading">
              <div className="dean-page-heading-left">
                <span className="dean-page-heading-line" />

                <span className="dean-page-heading-title">
                  {activeTitle}
                </span>
              </div>

              {!isMobile && (
                <span className="dean-page-heading-meta">
                  Zion Praise Academy - CRM School System • STUDENT AFFAIRS • Design by Polu
                </span>
              )}
            </div>

            <motion.div
              key={
                selectedUser
                  ? "student-user-details"
                  : activePage
              }
              initial={{
                opacity: 0,
                y: 6,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.2,
                ease: "easeOut",
              }}
              className={
                darkMode
                  ? "dean-workspace dean-workspace-dark"
                  : "dean-workspace"
              }
              style={{
                padding: isMobile
                  ? 9
                  : 15,
              }}
            >
              {renderPage()}
            </motion.div>
          </Content>
        </Layout>
      </Layout>

      {/* Password modal intentionally preserved as in your original page */}
      {/* 
      <Modal
        open={passwordSettingOpen}
        onCancel={() =>
          setPasswordSettingOpen(false)
        }
        footer={null}
        centered
        width={500}
        destroyOnClose
        className="dean-password-modal"
        title={null}
        styles={{
          content: {
            padding: 0,
            borderRadius: 12,
            overflow: "hidden",
          },
          body: {
            padding: 0,
          },
        }}
      >
        <ChangeUserPassword
          onClose={() =>
            setPasswordSettingOpen(false)
          }
        />
      </Modal>
      */}
    </div>
  );
};

export default DeanDashboard;

